import { CheckCircle2, Circle, Clock } from "lucide-react";

/**
 * Compact 5-step progress indicator for table rows.
 * Maps internal status → 5 customer-stage indices.
 */
const STAGE_BY_STATUS = {
  awaiting_arrival: 0,
  received: 1,
  awaiting_collection: 1,
  collected: 2,
  in_specification: 2,
  ready_for_return: 3,
  returned: 4,
  ready_for_dispatch: 4,
  dispatched: 5,
  delivered: 6,
  issue: 1,
};

const STAGE_NAMES = [
  "Order confirmed",
  "At warehouse",
  "With specialist",
  "Finishing up",
  "Quality checked",
  "Dispatched",
  "Delivered",
];

export const ProgressDots = ({ status, className = "" }) => {
  const current = STAGE_BY_STATUS[status] ?? 0;
  const total = STAGE_NAMES.length;
  return (
    <div
      className={`flex items-center gap-0.5 ${className}`}
      title={`Currently: ${STAGE_NAMES[current]} (stage ${current + 1} of ${total})`}
    >
      {Array.from({ length: total }).map((_, i) => {
        const done = i < current;
        const cur = i === current;
        const isDelivered = status === "delivered" && i === current;
        const state = done ? "Completed" : cur ? "In progress" : "Pending";
        return (
          <div
            key={i}
            className={`h-1.5 rounded-sm transition-all ${
              isDelivered
                ? "bg-emerald-600 w-5"
                : done
                ? "bg-zinc-900 w-4"
                : cur
                ? "bg-zinc-900 w-5"
                : "bg-zinc-200 w-3"
            }`}
            title={`${STAGE_NAMES[i]} · ${state}`}
          />
        );
      })}
    </div>
  );
};

/**
 * Large horizontal step bar — used in detail page and customer tracking page.
 * stages = [{ key, label, blurb?, date?, done, current, ... }]
 */
export const StepBar = ({ stages, hasIssue = false }) => {
  const total = stages.length || 1;
  const currentIdx = stages.findIndex((s) => s.current);
  const lastDoneIdx = stages.reduce((acc, s, i) => (s.done || s.current ? i : acc), -1);
  const isDelivered = stages[stages.length - 1]?.current;
  // Fill ratio: complete stages contribute fully; current-but-not-final contributes ~0.5
  const filled = isDelivered
    ? 1
    : lastDoneIdx <= 0
    ? 0
    : currentIdx > 0
    ? (currentIdx - 0.5) / (total - 1)
    : lastDoneIdx / (total - 1);
  const fillPct = Math.max(0, Math.min(100, filled * 100));
  const railColor = hasIssue ? "bg-rose-500" : isDelivered ? "bg-emerald-500" : "bg-zinc-950";
  // Precise inset so the rail starts at the first node centre and ends at the last
  const inset = `${50 / total}%`;

  return (
    <div className="w-full">
      {/* Desktop: horizontal */}
      <div className="hidden md:block">
        <ol
          className="relative grid gap-2"
          style={{ gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))` }}
        >
          {/* Connecting rail (background) — runs node-centre to node-centre */}
          <div
            className="absolute top-4 h-1 -translate-y-1/2 rounded-full bg-zinc-200"
            style={{ left: inset, right: inset }}
          />
          {/* Connecting rail (filled progress) */}
          <div
            className={`absolute top-4 h-1 -translate-y-1/2 rounded-full ${railColor} transition-all duration-700 ease-out`}
            style={{ left: inset, width: `calc((100% - ${inset} * 2) * ${fillPct / 100})` }}
          />
          {stages.map((s, idx) => (
            <li key={s.key} className="relative flex flex-col items-center text-center px-1">
              <div className="relative z-10 mb-2">
                <StepIcon
                  stage={s}
                  hasIssue={hasIssue}
                  isFinal={idx === total - 1}
                  finalActive={idx === total - 1 && (s.current || s.done)}
                />
              </div>
              <div
                className={`text-[11px] font-semibold uppercase tracking-wider ${
                  idx === total - 1 && s.current
                    ? "text-emerald-700"
                    : s.current
                    ? "text-zinc-950"
                    : s.done
                    ? "text-zinc-700"
                    : "text-zinc-400"
                }`}
              >
                {s.label}
              </div>
              {idx === total - 1 && s.current && (
                <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[9px] font-bold uppercase tracking-widest">
                  Complete
                </div>
              )}
              {s.date && (
                <div className="text-[10px] text-zinc-500 mt-0.5 font-mono">
                  {new Date(s.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                </div>
              )}
            </li>
          ))}
        </ol>
      </div>

      {/* Mobile: vertical */}
      <ol className="md:hidden space-y-3 relative pl-3">
        <div className="absolute left-[14px] top-3 bottom-3 w-px bg-zinc-200" />
        {stages.map((s, idx) => (
          <li key={s.key} className="flex items-start gap-3 relative">
            <StepIcon
              stage={s}
              hasIssue={hasIssue}
              small
              isFinal={idx === total - 1}
              finalActive={idx === total - 1 && (s.current || s.done)}
            />
            <div className="flex-1 pt-0.5">
              <div
                className={`text-sm font-semibold ${
                  idx === total - 1 && s.current
                    ? "text-emerald-700"
                    : s.current
                    ? "text-zinc-950"
                    : s.done
                    ? "text-zinc-700"
                    : "text-zinc-400"
                }`}
              >
                {s.label}
                {idx === total - 1 && s.current && (
                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[9px] font-bold uppercase tracking-widest">
                    Complete
                  </span>
                )}
              </div>
              {s.blurb && (s.current || s.done) && (
                <div className="text-xs text-zinc-500 mt-0.5">{s.blurb}</div>
              )}
              {s.date && (
                <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">
                  {new Date(s.date).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};

const StepIcon = ({ stage, hasIssue, small, isFinal, finalActive }) => {
  const size = small ? "h-7 w-7" : "h-8 w-8";
  // FINAL DELIVERED — celebratory emerald with halo
  if (isFinal && finalActive) {
    return (
      <div className={`${size} rounded-full bg-emerald-500 text-white flex items-center justify-center ring-4 ring-emerald-100 shadow-md shadow-emerald-200 relative`}>
        <CheckCircle2 className="h-4 w-4" strokeWidth={2.5} />
        {stage.current && (
          <span className="absolute -inset-1 rounded-full border-2 border-emerald-400 animate-ping opacity-60" />
        )}
      </div>
    );
  }
  if (stage.done && !stage.current) {
    return (
      <div className={`${size} rounded-full bg-zinc-950 text-white flex items-center justify-center ring-4 ring-zinc-50`}>
        <CheckCircle2 className="h-4 w-4" strokeWidth={2.5} />
      </div>
    );
  }
  if (stage.current) {
    return (
      <div className={`${size} rounded-full ${hasIssue ? "bg-rose-600" : "bg-zinc-950"} text-white flex items-center justify-center ring-4 ring-zinc-50 relative`}>
        <Clock className="h-4 w-4" strokeWidth={2.5} />
        <span className="absolute inset-0 rounded-full border-2 border-zinc-950 animate-ping opacity-40" />
      </div>
    );
  }
  return (
    <div className={`${size} rounded-full bg-white border-2 border-zinc-200 text-zinc-300 flex items-center justify-center ring-4 ring-zinc-50`}>
      <Circle className="h-3 w-3" strokeWidth={2.5} />
    </div>
  );
};

/**
 * Build the 5 customer-facing stages from an internal record.
 * Used on the detail page (admin) where the public-track endpoint isn't called.
 */
export function buildStagesFromRecord(r) {
  const currentIdx = STAGE_BY_STATUS[r?.status] ?? 0;
  const stages = [
    { key: "ordered", label: "Order confirmed", date: r?.created_at },
    { key: "in_warehouse", label: "At warehouse", date: r?.date_received || r?.date_ready_for_collection },
    { key: "with_specialist", label: "With specialist", date: r?.date_collected },
    { key: "finishing_up", label: "Finishing up", date: r?.date_ready_for_return },
    { key: "back_in_warehouse", label: "Back & checked", date: r?.date_returned },
    { key: "dispatched", label: "Dispatched", date: r?.date_dispatched },
  ];
  return stages.map((s, i) => ({
    ...s,
    done: i < currentIdx || (i === currentIdx && !!s.date) || i === 0,
    current: i === currentIdx,
  }));
}
